import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';
import {daysInStage, stageAgeColor} from '../utils';

export default function Pipeline() {
  const [applicants, setApplicants] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stages, setStages] = useState([]);
  const [jobFilter, setJobFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const load = () => {
    const url = jobFilter ? `/ats/applicants?jobId=${jobFilter}` : '/ats/applicants';
    api(url).then(r => r.ok ? r.json() : []).then(setApplicants).catch(() => {});
    api('/ats/jobs').then(r => r.ok ? r.json() : []).then(setJobs).catch(() => {});
    api('/ats/pipeline-stages').then(r => r.ok ? r.json() : []).then(setStages).catch(() => {});
  };
  useEffect(() => { load(); }, [jobFilter]);

  const COLORS = ['bg-gray-200', 'bg-yellow-100', 'bg-blue-100', 'bg-purple-100', 'bg-green-100', 'bg-red-100', 'bg-orange-100', 'bg-pink-100', 'bg-cyan-100'];

  const moveStage = async (applicantId, stage) => {
    await api(`/ats/applicants/${applicantId}`, { method: 'PUT', body: JSON.stringify({ stage }) });
    load();
  };

  const bulkMove = async (stage) => {
    if (selected.size === 0) return;
    await api('/ats/applicants/bulk-stage', { method: 'POST', body: JSON.stringify({ applicantIds: [...selected], stage }) });
    setSelected(new Set());
    load();
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(stageName);
  };

  const handleDragLeave = () => setDropTarget(null);

  const handleDrop = async (e, stageName) => {
    e.preventDefault();
    setDropTarget(null);
    if (dragId) {
      const applicant = applicants.find(a => a.id === dragId);
      if (applicant && applicant.stage !== stageName) {
        await moveStage(dragId, stageName);
      }
    }
    setDragId(null);
  };

  const handleDragEnd = () => { setDragId(null); setDropTarget(null); };

  const stageNames = stages.map(s => s.name.toLowerCase());

  return (
    <Layout>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Pipeline</h2>
        <div className="flex gap-2 items-center">
          <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="">All Jobs</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          {selected.size > 0 && (
            <select value="" onChange={e => { if (e.target.value) bulkMove(e.target.value); }} className="px-3 py-2 border border-teal-300 rounded-lg text-sm bg-teal-50">
              <option value="">Move {selected.size} selected...</option>
              {stageNames.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4 h-[calc(100vh-12rem)]">
        {stages.map((stage, idx) => {
          const name = stage.name.toLowerCase();
          const stageApplicants = applicants.filter(a => a.stage === name);
          const isDropping = dropTarget === name && dragId && applicants.find(a => a.id === dragId)?.stage !== name;
          return (
            <div key={stage.id || stage.name} className="min-w-[220px] flex-1 flex flex-col">
              <div className={`rounded-t-lg px-3 py-2 ${COLORS[idx % COLORS.length]}`}>
                <span className="text-sm font-semibold">{stage.name}</span>
                <span className="text-xs text-gray-500 ml-1">({stageApplicants.length})</span>
              </div>
              <div
                className={`bg-gray-50 rounded-b-lg p-2 space-y-2 flex-1 overflow-y-auto transition-colors ${isDropping ? 'bg-teal-50 ring-2 ring-teal-400 ring-inset' : ''}`}
                onDragOver={e => handleDragOver(e, name)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, name)}
              >
                {stageApplicants.map(a => (
                  <div
                    key={a.id}
                    draggable
                    onDragStart={e => handleDragStart(e, a.id)}
                    onDragEnd={handleDragEnd}
                    className={`bg-white rounded-lg shadow-sm p-3 border-l-4 cursor-grab active:cursor-grabbing transition-opacity ${
                      dragId === a.id ? 'opacity-40' : ''
                    } ${selected.has(a.id) ? 'border-teal-600 ring-1 ring-teal-300' : 'border-transparent'}`}
                  >
                    <div className="flex items-start gap-2">
                      <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)} className="mt-1 w-3.5 h-3.5 rounded border-gray-300 text-teal-600" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/ats/applicants/${a.id}`} className="text-sm font-medium text-gray-900 hover:text-teal-600 block truncate">{a.firstName} {a.lastName}</Link>
                        <p className="text-xs text-gray-500 truncate">{a.jobTitle || 'No position'}</p>
                        {a.updatedAt && <p className={`text-xs mt-0.5 ${stageAgeColor(a.updatedAt)}`}>{daysInStage(a.updatedAt)}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
